import { mutationOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AnswerRequest, Form } from "../dynamicForm";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import type { CreatedMeasure, CreateMeasurePayload, MeasuresGridView, MeasureById, Measure } from "./measures.types";
import { queryClient } from "./queryClient";

const BASE_ENDPOINT_MEASURE = `${FORMS_API_BASE_ENDPOINT}/measure`;

const measuresKeys = {
  all: () => ["measures"] as const,
  byId: (id: string) => [...measuresKeys.all(), id] as const,
  gridView: () => [...measuresKeys.all(), "gridview"] as const,
  form: () => [...measuresKeys.all(), "form"] as const,
};

/** Get measure grid view */
export const measuresGridViewQueryOptions = () =>
  queryOptions({
    queryKey: measuresKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT_MEASURE}/gridview`) as Promise<MeasuresGridView>,
    throwOnError: true,
  });

/** Get measures form */
export const measureFormQueryOptions = () =>
  queryOptions({
    queryKey: measuresKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT_MEASURE}/form`) as Promise<Form>,
    throwOnError: true,
  });

/** Get measures by id */
export const measureByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: measuresKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT_MEASURE}/${id}`) as Promise<MeasureById>,
    throwOnError: true,
  });

/** Update measure answers */
export const useUpdateMeasureAnswers = () => {
  const updateMeasureCache = useUpdateMeasureCache();

  return useMutation({
    mutationFn: ({ measureId, answers }: { measureId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT_MEASURE}/${measureId}/answers`, answers) as Promise<MeasureById>,
    onSuccess: updateMeasureCache,
  });
};

/** Update measures cache */
const useUpdateMeasureCache = () => {
  const queryClient = useQueryClient();

  return (updatedMeasure: MeasureById) => {
    /** Update measure by id  */
    queryClient.setQueryData(measuresKeys.byId(updatedMeasure.id), updatedMeasure);

    /** Update measure grid view */
    queryClient.setQueryData(measuresKeys.gridView(), (measureGridView: MeasuresGridView | undefined) => {
      if (!measureGridView) return undefined;
      return {
        ...measureGridView,
        measures: measureGridView.measures.map(gridViewMeasure =>
          updatedMeasure.id === gridViewMeasure.id
            ? {
                ...gridViewMeasure,
                ...updatedMeasure,
                connectedDpias: gridViewMeasure.connectedDpias,
                connectedRisks: gridViewMeasure.connectedRisks,
              }
            : gridViewMeasure
        ),
      } satisfies MeasuresGridView;
    });
  };
};

/** Create measure */
export const createMeasureMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: CreateMeasurePayload) => api.post(BASE_ENDPOINT_MEASURE, payload) as Promise<CreatedMeasure>,
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Delete measure */
export const deleteMeasureMutationOptions = () =>
  mutationOptions({
    mutationFn: (measureId: string) => api.delete(`${BASE_ENDPOINT_MEASURE}/${measureId}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Get Measures */
export const measuresQueryOptions = () =>
  queryOptions({
    queryKey: measuresKeys.all(),
    queryFn: () => api.get(BASE_ENDPOINT_MEASURE) as Promise<Measure[]>,
    throwOnError: true,
  });

/** Update Measure */
export const updateMeasureMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ measureId, payload }: { measureId: string; payload: CreateMeasurePayload }) =>
      api.patch(`${BASE_ENDPOINT_MEASURE}/${measureId}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });
